import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { StoreFeatures } from '@state/store.utils';
import { SubscriptionActions } from '@state/subscription/subscription.actions';
import {
  ContractResponse,
  InvoiceCallDetailsResponse,
  InvoiceInternetDetailsResponse,
  MsisdnSubscriptionDetails,
  ProductCatalogSubscription,
  SubscriptionResponse,
  Usage,
  WalletResponse,
} from '@core/contracts/data-contracts';
import {
  ActionStatus,
  CallSmsUsageViewModel,
  DefaultData,
  InternetUsageViewModel,
} from '@core/models';
import { UserActions } from '@state';

export type SubscriptionState = {
  subscription: SubscriptionResponse;
  contract: ContractResponse;
  costs: MsisdnSubscriptionDetails[];
  internetUsage: InternetUsageViewModel | null;
  callSmsUsage: CallSmsUsageViewModel | null;
  totalPrice: number;
  isLoading: boolean;

  currentBundles: Usage[];
  isCurrentBundlesLoading: boolean;
  extraBundles: ProductCatalogSubscription[];

  wallet: WalletResponse | null;
  walletTopupUrl: string | null;
  walletInternetUsage: InvoiceInternetDetailsResponse | null;
  walletCallSmsUsage: InvoiceCallDetailsResponse | null;

  updateSubscriptionLoading: boolean;
  isStickyElementVisible: boolean;

  updateSubscriptionStatus: ActionStatus | null;
};

export const initialSubscriptionState: SubscriptionState = {
  subscription: DefaultData.subscription,
  contract: DefaultData.contract,
  costs: [],
  internetUsage: null,
  callSmsUsage: null,
  totalPrice: 0,
  isLoading: false,

  currentBundles: [],
  isCurrentBundlesLoading: true,
  extraBundles: [],

  wallet: null,
  walletTopupUrl: null,
  walletInternetUsage: null,
  walletCallSmsUsage: null,

  updateSubscriptionLoading: false,
  isStickyElementVisible: false,

  updateSubscriptionStatus: null,
};

export const subscriptionReducer = createReducer(
  initialSubscriptionState,
  on(
    SubscriptionActions.isStickyElementVisible,
    (state, { isStickyElementVisible }): SubscriptionState => ({
      ...state,
      isStickyElementVisible,
    })
  ),
  on(
    SubscriptionActions.getCurrentSubscription,
    (state): SubscriptionState => ({
      ...state,
      isLoading: true,
    })
  ),
  on(
    SubscriptionActions.getCurrentSubscriptionFailure,
    (state): SubscriptionState => ({
      ...state,
      isLoading: false,
    })
  ),
  on(
    SubscriptionActions.getCurrentSubscriptionSuccess,
    (state, { subscription }): SubscriptionState => ({
      ...state,
      subscription,
      isLoading: false,
    })
  ),
  on(
    SubscriptionActions.getContractDetailsSuccess,
    (state, { contract }): SubscriptionState => ({
      ...state,
      contract,
    })
  ),
  on(
    SubscriptionActions.getCostsDetailsSuccess,
    (state, { costs, totalPrice }): SubscriptionState => ({
      ...state,
      costs,
      totalPrice,
    })
  ),
  on(
    SubscriptionActions.getInternetUsageDetailsSuccess,
    (state, { internetUsage }): SubscriptionState => ({
      ...state,
      internetUsage,
    })
  ),
  on(
    SubscriptionActions.getCallSmsUsageDetailsSuccess,
    (state, { callSmsUsage }): SubscriptionState => ({
      ...state,
      callSmsUsage,
    })
  ),
  on(
    SubscriptionActions.getBundlesSuccess,
    (state, { bundles: { bought, available } }): SubscriptionState => ({
      ...state,
      currentBundles: bought,
      isCurrentBundlesLoading: false,
      extraBundles: available,
    })
  ),
  on(
    SubscriptionActions.getWalletSuccess,
    (state, { wallet }): SubscriptionState => ({
      ...state,
      wallet,
    })
  ),
  on(
    SubscriptionActions.topupWalletSuccess,
    (state, { url }): SubscriptionState => ({
      ...state,
      walletTopupUrl: url,
    })
  ),
  on(
    SubscriptionActions.clearWalletTopupUrl,
    (state): SubscriptionState => ({
      ...state,
      walletTopupUrl: null,
    })
  ),
  on(
    SubscriptionActions.getWalletInternetUsageSuccess,
    (state, { internetUsage }): SubscriptionState => ({
      ...state,
      walletInternetUsage: internetUsage,
    })
  ),
  on(
    SubscriptionActions.getWalletVoiceUsageSuccess,
    (state, { callSmsUsage }): SubscriptionState => ({
      ...state,
      walletCallSmsUsage: callSmsUsage,
    })
  ),
  on(
    SubscriptionActions.updateSubscription,
    (state): SubscriptionState => ({
      ...state,
      updateSubscriptionLoading: true,
      updateSubscriptionStatus: null,
    })
  ),
  on(
    SubscriptionActions.updateSubscriptionSuccess,
    (state): SubscriptionState => ({
      ...state,
      updateSubscriptionLoading: false,
      updateSubscriptionStatus: ActionStatus.Success,
    })
  ),
  on(
    SubscriptionActions.updateSubscriptionFailure,
    (state): SubscriptionState => ({
      ...state,
      updateSubscriptionLoading: false,
      updateSubscriptionStatus: ActionStatus.Failed,
    })
  ),
  on(
    SubscriptionActions.clearUpdateSubscriptionStatus,
    (state): SubscriptionState => ({
      ...state,
      updateSubscriptionStatus: null,
    })
  ),

  on(UserActions.signOut, (): SubscriptionState => initialSubscriptionState)
);

export const subscriptionFeature = createFeature({
  name: StoreFeatures.Subscription,
  reducer: subscriptionReducer,
  extraSelectors: ({ selectSubscription }) => ({
    selectContractStartDate: createSelector(
      selectSubscription,
      (subscription) => subscription?.startDate
    ),
  }),
});
